// frontend/features/mpc-common/src/features/Bidding/SupplierQuoteDrawer/handleColumns.tsx
import React, { useState, useEffect } from 'react';
import {
  Button,
  Space,
  Select,
  Popover,
  DatePicker,
} from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { localPx2rem } from '@mpc/common/src/utils/index';
import TextAreaWithUpload from '@mpc/common/src/components/SForm/SFormElement/TextAreaWithUpload';
import {
  getRequiredRule,
  getOptionsRule,
  getLogicOptions,
  getConditionDisabled,
} from '../../utils';
import type {
  BiddingQuoteTemplateColumnVo,
  SupplierQuoteDetailRow,
  ViewMode,
  QuoteColumn,
  ColumnComputedConfig,
} from './types';
import type { SupplierQuoteTab } from '../../../services/api';

// ========== 工具函数 ==========

/** 千分位格式化 */
const thousandthFormat = (value: number | string) => {
  if (value === undefined) return '';
  const [intPart, decimalPart] = `${value}`.split('.');
  const hasDecimal = value.toString().includes('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return hasDecimal ? `${formattedInt}.${decimalPart}` : formattedInt;
};

/**
 * 安全四则运算公式求值。
 * expression 由后端生成（非用户输入，注入风险极低），
 * 将依赖变量替换为 row 中的实际值后执行。
 *
 * @returns 计算结果，依赖值不合法时返回 null
 */
export function evaluateFormula(
  expression: string,
  dependencies: string[],
  row: Record<string, any>
): number | null {
  let expr = expression;

  // 1. 按依赖名长度降序排列，避免短名误替换长名中的子串（如 tax 误改 tax_rate）
  const sorted = [...dependencies].sort((a, b) => b.length - a.length);

  for (const dep of sorted) {
    const val = Number(row[dep]);
    if (isNaN(val)) return null;
    expr = expr.replaceAll(dep, String(val));
  }

  // 2. 全部替换完成后安全检查：剩余字符只能是 数字、运算符、括号、空格、小数点
  const allowed = /^[\d+\-*/().%\s]+$/;
  if (!allowed.test(expr)) return null;

  // 3. 安全求值
  try {
    return Function(`"use strict"; return (${expr})`)();
  } catch {
    return null;
  }
}

/** 深层合并: key 相同且是对象则合并, 否则覆盖 */
const objectDeepMerge = (obj1: any, obj2: any): any => {
  if (!obj1 || !obj2 || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj2;
  }
  const newObj = Object.assign({}, obj1);
  Object.keys(obj2).forEach((key) => {
    if (!(key in obj1)) {
      newObj[key] = obj2[key];
      return;
    }
    if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
      if (Array.isArray(obj1[key]) || Array.isArray(obj2[key])) {
        newObj[key] = obj2[key];
      } else {
        newObj[key] = objectDeepMerge(obj1[key], obj2[key]);
      }
    } else {
      newObj[key] = obj2[key];
    }
  });
  return newObj;
};

/** 合并多个 render 函数 */
const mergeRenderFn = (...renderFnList: any[]) => {
  return (...args: any[]) => {
    let renderConfig = {};
    for (const renderFn of renderFnList) {
      renderConfig = objectDeepMerge(
        renderConfig,
        typeof renderFn === 'function' ? renderFn(...args) : renderFn
      );
    }
    return renderConfig;
  };
};

/** 统一添加校验规则（非编辑列跳过必填规则） */
const hanleColumnRule = (
  header: BiddingQuoteTemplateColumnVo,
  editRender: any,
  canEditColumnCategories?: number[],
  respectOutsourceModify?: boolean
) => {
  return ({ value, row, index }: any) => {
    const editConfig = editRender({ value, row, index });
    const conditionDisabled = getConditionDisabled(header as any, row);
    // 白名单模式下，非编辑列不添加必填规则（undefined=默认全部可编辑）
    // 供应商侧（outer）：列级 outsource_modify=true 的列也视为可编辑
    const isEditable =
      canEditColumnCategories === undefined ||
      canEditColumnCategories.includes(header.category) ||
      (respectOutsourceModify && !!header.outsource_modify);
    const requireRule = isEditable ? getRequiredRule(header, row) : [];

    const newEditConfig = {
      ...editConfig,
      required: requireRule.length > 0,
      rule: [...requireRule, ...(editConfig?.rule || [])],
    };
    if (conditionDisabled && newEditConfig?.props && !value?.toString?.()) {
      newEditConfig.props = {
        ...newEditConfig.props,
        disabled: true,
        key: 'disabled',
      };
    }
    return newEditConfig;
  };
};

// ========== 透传行操作回调到 operateColumn ==========

/**
 * BatchEditWrapper 的 DATE_MAP 对 date_range 数组值有 bug（dayjs(数组).format()），
 * 用本组件包装 DatePicker，onChange 时提前将 [d1,d2] 转为格式化字符串数组，
 * 并改用 name='dateRange' 避开 BatchEditWrapper 的 DATE_MAP 检查。
 */
const DateRangeBatchEdit = (props: any) => {
  const { onChange, ...rest } = props;
  return (
    <DatePicker
      {...rest}
      range
      onChange={(_dates: any, dateStrings: string[]) => {
        onChange?.(dateStrings);
      }}
    />
  );
};

const DateBatchEdit = (props: any) => {
  const { onChange, ...rest } = props;
  return (
    <DatePicker
      {...rest}
      onChange={(_date: any, dateString: string) => {
        onChange?.(dateString);
      }}
    />
  );
};

// ========== 透传行操作回调到 operateColumn ==========

export interface RowOperationCallbacks {
  onAddRow?: (afterIndex: number) => void;
  onCopyRow?: (row: any) => void;
  onDeleteRow?: (rowIndex: number) => void;
}

// ========== Schema → Column 核心映射 ==========

const defaultWidth = 200;

function isCellReadOnly(
  col: BiddingQuoteTemplateColumnVo,
  row: SupplierQuoteDetailRow,
  viewMode: ViewMode,
  canEditColumnCategories?: number[],
  respectOutsourceModify?: boolean
): boolean {
  if (viewMode !== 'edit') return true;
  // category: 0=标段字段, 1=方案设计字段, 2=报价字段, 3=报价固定字段

  // 白名单模式：传了 canEditColumnCategories（即使空数组）→ 只有集合内的 category 可编辑
  // undefined → 沿用默认规则（向后兼容旧调用方）
  // 注意：白名单模式下不检查 row.outsource_modify — 白名单用于甲方（买方）编辑场景，
  // outsource_modify 仅在供应商侧的默认规则中生效。
  if (canEditColumnCategories !== undefined) {
    // 供应商侧（outer）：列级 outsource_modify=true 的列也可编辑
    if (respectOutsourceModify && col.outsource_modify) return false;
    if (!canEditColumnCategories.includes(col.category)) return true;
    return false;
  }

  // 默认规则（未传白名单时兜底，兼容 Drawer / PendingAssess 等旧调用方）
  if (col.category === 0 || col.category === 3) return true;
  if (col.category === 1 && !row.outsource_modify) return true;
  return false;
}

function headerTypeToColumnConfig({
  header,
  index,
  viewMode,
  readOnly,
  canEditColumnCategories,
  respectOutsourceModify,
}: {
  header: BiddingQuoteTemplateColumnVo;
  index: number;
  viewMode: ViewMode;
  readOnly?: boolean;
  canEditColumnCategories?: number[];
  respectOutsourceModify?: boolean;
}) {
  const { type: data_type, code, label, category } = header;

  let defaultEditAble = viewMode === 'edit' && !readOnly;
  // 白名单模式：只有集合内的 category 可编辑（undefined=默认规则，[]=全部只读）
  // 供应商侧（outer）：列级 outsource_modify=true 也可编辑
  if (canEditColumnCategories !== undefined) {
    defaultEditAble =
      defaultEditAble &&
      (canEditColumnCategories.includes(category) ||
        (respectOutsourceModify && !!header.outsource_modify));
  } else {
    // 默认规则：category 0 (标段) / 3 (报价固定) 不可编辑
    if (category === 0 || category === 3) {
      defaultEditAble = false;
    }
  }
  if (category === 1 && canEditColumnCategories === undefined) {
    // 默认模式下：方案设计字段由行级 outsource_modify 控制（供应商侧）
    defaultEditAble = true;
  }

  // 非编辑列不显示必填 * 标记（undefined=默认全部可编辑）
  const isCategoryEditable =
    canEditColumnCategories === undefined ||
    canEditColumnCategories.includes(category);

  // title 用纯字符串（ProTablePlusForm 用它构造校验错误消息，JSX → [object Object]）
  // 红色 * 通过 className + CSS ::before 实现
  let config: any = {
    title: label,
    dataIndex: header.code,
  };

  // 必填列添加 className，CSS ::before 渲染红色 *
  if (header.required_mode === 1 && !readOnly && isCategoryEditable) {
    config.className = 'bidding-quote-required-column';
  }

  // 根据 data_type 处理编辑渲染
  switch (data_type) {
    case 'text':
      config = {
        width: 400,
        ...config,
        editRender: mergeRenderFn(({ value, row }: any) => {
          const cellReadOnly = isCellReadOnly(
            header,
            row,
            viewMode,
            canEditColumnCategories,
            respectOutsourceModify
          );
          return {
            name: 'mpc-textArea',
            editable: !cellReadOnly,
            rule: [
              {
                validator: (_rule: any, val: any, callback: any) => {
                  if (val && val.length > 500) {
                    callback('最多500字');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: {
              placeholder: '请输入',
              maxLength: 500,
            },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          () => ({
            name: 'input',
            editable: defaultEditAble,
            props: {
              placeholder: '请输入',
              maxLength: 500,
            },
          }),
          config.batchEditRender || {}
        ),
      };
      break;

    case 'dropdown_list':
      config = {
        width: 200,
        ...config,
        editRender: mergeRenderFn(({ value, row }: any) => {
          const options = getLogicOptions(header as any, row);
          const cellReadOnly = isCellReadOnly(
            header,
            row,
            viewMode,
            canEditColumnCategories,
            respectOutsourceModify
          );
          return {
            name: 'select',
            editable: !cellReadOnly,
            rule: [...getOptionsRule(options)],
            props: {
              allowClear: true,
              multiple: true,
              options,
              autoWidth: false,
              showSearch: true,
              style: { maxWidth: localPx2rem(400) },
              dropdownStyle: {
                minWidth: localPx2rem(280),
                maxWidth: localPx2rem(400),
              },
            },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          () => ({
            name: 'select',
            editable: defaultEditAble,
            props: {
              allowClear: true,
              multiple: true,
              options: getLogicOptions(header as any, 'all'),
              autoWidth: false,
              showSearch: true,
              style: { maxWidth: localPx2rem(400) },
              dropdownStyle: {
                minWidth: localPx2rem(280),
                maxWidth: localPx2rem(400),
              },
            },
          }),
          config.batchEditRender || {}
        ),
      };
      break;

    case 'dropdown_radio':
      config = {
        width: 200,
        ...config,
        editRender: mergeRenderFn(({ value, row }: any) => {
          const options = getLogicOptions(header as any, row);
          const cellReadOnly = isCellReadOnly(
            header,
            row,
            viewMode,
            canEditColumnCategories,
            respectOutsourceModify
          );
          return {
            name: 'select',
            editable: !cellReadOnly,
            rule: [...getOptionsRule(options)],
            props: {
              allowClear: true,
              options,
              autoWidth: true,
              showSearch: true,
              style: { maxWidth: localPx2rem(400) },
              dropdownStyle: {
                minWidth: localPx2rem(280),
                maxWidth: localPx2rem(400),
              },
            },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          () => ({
            name: 'select',
            editable: defaultEditAble,
            props: {
              allowClear: true,
              options: getLogicOptions(header as any, 'all'),
              autoWidth: true,
              showSearch: true,
              style: { maxWidth: localPx2rem(400) },
              dropdownStyle: {
                minWidth: localPx2rem(280),
                maxWidth: localPx2rem(400),
              },
            },
          }),
          config.batchEditRender || {}
        ),
      };
      break;

    case 'numeric':
      config = {
        width: 160,
        ...config,
        render(value: any, _row: any, _idx: number) {
          if (value === undefined || value === null) return '--';
          const suffix = data_type === 'percent' ? '%' : '';
          return (
            <div style={{ textAlign: 'right' }}>
              {thousandthFormat(Number(value)?.toFixed?.(2))}
              {suffix}
            </div>
          );
        },
        editRender: mergeRenderFn(({ value, row }: any) => {
          const cellReadOnly = isCellReadOnly(
            header,
            row,
            viewMode,
            canEditColumnCategories,
            respectOutsourceModify
          );
          return {
            name: 'inputNumber',
            editable: !cellReadOnly,
            rule: [
              {
                validator: (_rule: any, val: any, callback: any) => {
                  if (!!val && isNaN(+val)) {
                    callback('请输入数字');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: {
              formatter: (v: any) => thousandthFormat(v),
              parser: (v: any) => v?.replace?.(/\s?|(,*)/g, '') ?? v,
              toFixed: 4,
              placeholder: '请输入',
              defaultValue: '',
              direction: 'vertical',
            },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          () => ({
            name: 'inputNumber',
            editable: defaultEditAble,
            props: {
              toFixed: 4,
              placeholder: '请输入',
              direction: 'vertical',
            },
          }),
          config.batchEditRender || {}
        ),
      };
      break;

    case 'percentage':
      config = {
        width: 160,
        ...config,
        render: (value: any, _row: any, _idx: number) => {
          if (value === undefined || value === null || value === '')
            return '--';
          return (
            <div style={{ textAlign: 'right' }}>
              {(Number(value) * 100).toFixed(2)}%
            </div>
          );
        },
        editRender: mergeRenderFn(({ value, row }: any) => {
          const cellReadOnly = isCellReadOnly(
            header,
            row,
            viewMode,
            canEditColumnCategories,
            respectOutsourceModify
          );
          return {
            name: 'inputPercent',
            editable: !cellReadOnly,
            props: {
              placeholder: '请输入',
              direction: 'vertical',
              digits: 2,
              max: null,
            },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          () => ({
            name: 'inputPercent',
            editable: defaultEditAble,
            props: {
              placeholder: '请输入',
              direction: 'vertical',
            },
          }),
          config.batchEditRender || {}
        ),
      };
      break;

    case 'date_input':
      config = {
        width: 180,
        ...config,
        render(value: any) {
          return value ? dayjs(value).format('YYYY-MM-DD') : '--';
        },
        editRender: mergeRenderFn(({ value, row }: any) => {
          const cellReadOnly = isCellReadOnly(
            header,
            row,
            viewMode,
            canEditColumnCategories,
            respectOutsourceModify
          );
          return {
            name: 'date',
            editable: !cellReadOnly,
            rule: [
              {
                validator: (_rule: any, val: any, callback: any) => {
                  if (!!val && !dayjs(val).isValid()) {
                    callback('请输入日期');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: { placeholder: '请选择日期' },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          () => ({
            name: 'dateInput',
            component: DateBatchEdit,
            editable: defaultEditAble,
            props: { placeholder: '请选择日期', format: 'YYYY-MM-DD' },
          }),
          config.batchEditRender || {}
        ),
      };
      break;

    case 'date_range':
      config = {
        width: 280,
        ...config,
        render(value: any) {
          return value?.join?.(' ~ ') || '--';
        },
        editRender: mergeRenderFn(({ value, row }: any) => {
          const cellReadOnly = isCellReadOnly(
            header,
            row,
            viewMode,
            canEditColumnCategories,
            respectOutsourceModify
          );
          return {
            name: 'date',
            editable: !cellReadOnly,
            rule: [
              {
                validator: (_rule: any, val: any, callback: any) => {
                  if (
                    val?.some?.((n: any) => {
                      if (!!n && !dayjs(n).isValid()) return true;
                      return false;
                    })
                  ) {
                    callback('请输入日期');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: { range: true, placeholder: ['开始日期', '结束日期'] },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          () => ({
            name: 'dateRange',
            component: DateRangeBatchEdit,
            editable: defaultEditAble,
            props: {
              placeholder: ['开始日期', '结束日期'],
              format: 'YYYY-MM-DD',
            },
          }),
          config.batchEditRender || {}
        ),
      };
      break;

    case 'text_with_upload':
      config = {
        width: 380,
        ...config,
        render(value: any) {
          if (!value) return '--';
          return <TextAreaWithUpload value={value} view />;
        },
        editRender: mergeRenderFn(({ value, row }: any) => {
          const cellReadOnly = isCellReadOnly(
            header,
            row,
            viewMode,
            canEditColumnCategories,
            respectOutsourceModify
          );
          return {
            name: 'text_with_upload',
            editable: !cellReadOnly,
            props: {
              placeholder: '请输入',
              view: cellReadOnly,
            },
          };
        }, config.editRender || {}),
        batchEditRender: null,
      };
      break;

    case 'input':
    default:
      config = {
        width: 200,
        ...config,
        editRender: mergeRenderFn(({ value, row }: any) => {
          const cellReadOnly = isCellReadOnly(
            header,
            row,
            viewMode,
            canEditColumnCategories,
            respectOutsourceModify
          );
          return {
            name: 'input',
            editable: !cellReadOnly,
            rule: [
              {
                validator: (_rule: any, val: any, callback: any) => {
                  if (!!val && val.length > 50) {
                    callback('最多50字');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: { placeholder: '请输入', maxLength: 50 },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          () => ({
            name: 'input',
            editable: defaultEditAble,
            props: { placeholder: '请输入', maxLength: 50 },
          }),
          config.batchEditRender || {}
        ),
      };
  }

  if (!config.width) {
    config.width = defaultWidth;
  }

  // 文本+上传列 只读时跳过后续处理
  if (data_type === 'text_with_upload' && readOnly) {
    return config;
  }

  if (!config.render) {
    // 默认 render: 数组 join, 空值显示 --
    config.render = (value: any) => {
      if (Array.isArray(value)) {
        return !value?.length ? '--' : value?.join(',');
      }
      return !value ? '--' : String(value);
    };
  }

  // 只读态: 关闭编辑
  if (readOnly || viewMode !== 'edit') {
    defaultEditAble = false;
    config.editRender = null;
    config.batchEditRender = null;
  }

  // 统一处理校验规则
  if (defaultEditAble && config.editRender) {
    config.editRender = hanleColumnRule(
      header,
      config.editRender,
      canEditColumnCategories,
      respectOutsourceModify
    );
  } else {
    config.batchEditRender = null;
    config.editRender = null;
  }

  return config;
}

// ========== operateColumn: 行操作列 ==========

function buildOperateColumn({
  viewMode,
  rowOps,
}: {
  viewMode: ViewMode;
  rowOps?: RowOperationCallbacks;
}) {
  if (viewMode !== 'edit' || !rowOps) return null;

  return {
    title: '操作',
    width: 116,
    dataIndex: '$operate',
    fixed: 'right' as const,
    render: (_v: any, row: any, rowIndex: number) => {
      return (
        <Space size={4}>
          <Button
            type="text"
            danger
            onClick={() => rowOps.onDeleteRow?.(rowIndex)}
          >
            删除
          </Button>
        </Space>
      );
    },
  };
}

// ========== 提交前补全计算列 ==========

/**
 * 按列类型精简小数位，消除浮点精度问题。
 * percentage → 4 位，其他 → 2 位
 */
function roundByType(val: number, colType?: string): number {
  const precision = colType === 'percentage' ? 4 : 2;
  return Number(val.toFixed(precision));
}

/**
 * 提交前遍历所有 tab 的计算列，将公式结果写入行数据。
 * 确保 API 收到的数据完整（计算列的值虽不展示在编辑控件中，但需出现在提交数据里）。
 */
export function fillComputedColumns(
  tabs: SupplierQuoteTab[]
): SupplierQuoteTab[] {
  return tabs.map((tab) => {
    const computedCols = (tab.columns || []).filter(
      (c) => (c as QuoteColumn).computed
    ) as QuoteColumn[];
    if (!computedCols.length) return tab;

    return {
      ...tab,
      rows: tab.rows?.map((row) => {
        const filled = { ...row };
        for (const col of computedCols) {
          const { expression, dependencies } = col.computed!;
          const val = evaluateFormula(expression, dependencies, row);
          if (val !== null) {
            filled[col.code!] = roundByType(val, col.type);
          }
        }
        return filled;
      }),
    };
  });
}

// ========== 公开: buildColumns ==========

export interface BuildColumnsArgs {
  schemaColumns: QuoteColumn[];
  viewMode: ViewMode;
  readOnly?: boolean;
  rowOps?: RowOperationCallbacks;
  showInsertOperation?: boolean;
  showCopyOperation?: boolean;
  /** 允许编辑的 category 集合（可选，不传则按默认规则：0/3 只读，1/2 可编辑） */
  canEditColumnCategories?: number[];
  /** 供应商侧（outer）编辑态：尊重列级 outsource_modify 字段控制可编辑性 */
  respectOutsourceModify?: boolean;
}

/**
 * 把后端 schema 转为 ProTablePlusForm 可消费的平铺列定义
 *
 * 规则（按后端 BiddingQuoteTemplateColumnVo.category 数字）:
 * - category 0 (标段字段) / 3 (报价固定字段) → 永远只读
 * - category 1 (方案设计字段) → 按 row.outsource_modify 行级控制
 * - category 2 (报价字段) → 在 edit 态恒可编辑 (除非 readOnly)
 * - view/history 态所有列只读
 */
export function buildColumns({
  schemaColumns,
  readOnly,
  rowOps,
  canEditColumnCategories,
  respectOutsourceModify,
  viewMode,
}: BuildColumnsArgs) {
  // 增加data_type, 对齐BiddingQuoteTemplateColumnVo和DemandQuoteTableColumnDto, 因为上面import的'../../utils'的函数是复用的
  schemaColumns.map((item) => ({ ...item, data_type: item.type }));

  const dataColumns = schemaColumns.map((header, index) =>
    headerTypeToColumnConfig({
      header: header as BiddingQuoteTemplateColumnVo,
      index,
      viewMode,
      readOnly,
      canEditColumnCategories,
      respectOutsourceModify,
    })
  );

  // 计算列覆盖：不可编辑 + 公式渲染
  const processedColumns = dataColumns.map((col, i) => {
    const header = schemaColumns[i];
    if (!header.computed) return col;

    const { expression, dependencies } = header.computed;

    // 输出格式：取列自身的 type 作为展示格式（percentage → ×100 + %，numeric → 千分位）
    const renderByType = header.type;

    return {
      ...col,
      editRender: undefined,
      batchEditRender: undefined,
      render: (_value: any, row: any) => {
        const result = evaluateFormula(expression, dependencies, row);
        if (result === null) return '--';
        const rounded = roundByType(result, renderByType);
        if (renderByType === 'percentage') {
          return (
            <div style={{ textAlign: 'right' }}>
              {(rounded * 100).toFixed(2)}%
            </div>
          );
        }
        return (
          <div style={{ textAlign: 'right' }}>{thousandthFormat(rounded)}</div>
        );
      },
    };
  });

  const operateColumn = buildOperateColumn({
    viewMode,
    rowOps,
  });

  return operateColumn
    ? [...processedColumns, operateColumn]
    : processedColumns;
}
